/**
 * Denaro in centesimi interi.
 *
 * Otto applica percentuali ("alza del 15%") e le somma su centinaia di notti:
 * in virgola mobile la deriva è inevitabile e finisce in un prezzo pubblicato.
 * Internamente quindi solo interi; la conversione da/verso i decimali del PMS
 * avviene esclusivamente nel connettore.
 */

export type Cents = number;

/**
 * Due formattatori perché «2.434,50 €» e «240 €» sono entrambi giusti e
 * «2434,5 €» no: l'italiano di CLDR non separa le migliaia sotto i diecimila
 * e lascia un decimale solo, che su un rendiconto sembra un errore.
 */
const EUR_INTERI = new Intl.NumberFormat("it-IT", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  useGrouping: "always",
} as Intl.NumberFormatOptions);

const EUR = new Intl.NumberFormat("it-IT", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: "always",
} as Intl.NumberFormatOptions);

const EUR_SIGNED = new Intl.NumberFormat("it-IT", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  signDisplay: "exceptZero",
});

export function toCents(amount: number): Cents {
  return Math.round(amount * 100);
}

export function fromCents(cents: Cents): number {
  return Math.round(cents) / 100;
}

/** "€115" oppure "€115,50" — mai "€115,00". */
export function formatEur(cents: Cents): string {
  const euro = fromCents(cents);
  return Number.isInteger(euro) ? EUR_INTERI.format(euro) : EUR.format(euro);
}

/** "+€20" / "−€15" — per i delta mostrati nelle anteprime. */
export function formatEurDelta(cents: Cents): string {
  return EUR_SIGNED.format(fromCents(cents)).replace("-", "−");
}

/**
 * Applica una variazione percentuale arrotondando all'euro intero.
 *
 * I property manager pubblicano prezzi tondi: un +15% su 95€ deve dare 109€,
 * non 109,25€. `roundToCents` permette di cambiare la granularità.
 */
export function applyPercent(
  cents: Cents,
  percent: number,
  roundToCents: Cents = 100,
): Cents {
  const raw = cents * (1 + percent / 100);
  return Math.round(raw / roundToCents) * roundToCents;
}

export function clampToFloor(
  cents: Cents,
  floor: Cents | null | undefined,
): Cents {
  return floor == null ? cents : Math.max(cents, floor);
}

/** "15%" / "-7,5%" */
export function formatPercent(percent: number): string {
  const rounded = Math.round(percent * 10) / 10;
  return `${rounded.toString().replace(".", ",")}%`;
}
